import { recordListConfigFrom } from "@smartsuite/automation-conditions";
import {
  DropdownPanel,
  Icon,
  MenuGroupContent,
  MenuOption,
  Paragraph,
  SelectPlaceholder,
  Text,
} from "@smartsuite/react-ui/lib";
import {
  StyledHeader,
  StyledSelector,
  StyledSelectorInput,
  StyledWrapper,
} from "./styles";
import { getLocalCssColorVariables } from "../../../../../../utils/cssVariablesUtils";
import { useTheme } from "../../../../../../stores";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../../../../../stores/hooks";
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import classNames from "classnames";
import { FieldMissingError } from "../../../ErrorSection";
import { outputReferenceToOptionValue } from "./get-available-webhook-outputs";
import type {
  LoopRecordListActionGroupConfig,
  OutputDescription,
} from "@smartsuite/automation-engine-api";
import type { LoopingStore } from "../../../../../../stores/automation/looping.store";
import {
  ActionLabel,
  CURRENT_ITEM_OUTPUT_ID,
  RECORD_CREATED_TRIGGER_IDS,
} from "../../../../../../utils/constants";
import { isSingleFindRecordsAction } from "../../../../Forms/InputForm/RecordsSourceEditor/utils";
import {
  buildOutputMenuOptions,
  getOutputLabel,
} from "./build-output-menu-options";
import {
  StyledIcon,
  StyledNoFields,
} from "../../../../Forms/InputForm/FieldEditor/FieldEditorRow/containers/TriggerOptions/style";
import { SolutionColorVariables } from "@smartsuite/types";

const WHOLE_RECORD_VALUE = "__whole_record__";

export const SelectWebhookOutput = observer<{ loopInstanceId?: number }>(
  ({ loopInstanceId }) => {
    const theme = useTheme();
    const candidate = useRootStore().automations.candidate;
    const looping = candidate.looping;
    const loopSource =
      loopInstanceId !== undefined
        ? looping.sourceAt(loopInstanceId)
        : looping.source;
    const setLoopSource = (
      next?: LoopRecordListActionGroupConfig["source"],
    ): void => {
      if (loopInstanceId !== undefined) {
        looping.setSourceAt(loopInstanceId, next);
      } else {
        looping.setSource(next);
      }
    };
    const buttonRef = useRef<HTMLDivElement>(null);
    const getSelectedOutputValue = (): string | null => {
      if (loopSource?.oneofKind === "outputReference") {
        return loopSource.outputReference.outputId;
      }
      if (
        loopSource?.oneofKind === "recordList" &&
        loopSource.recordList.recordList.oneofKind === "actionReference"
      ) {
        return WHOLE_RECORD_VALUE;
      }
      return null;
    };
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [tab, setTab] = useState<"root" | "sub-menu">("root");
    const [subMenuTitle, setSubMenuTitle] = useState("");
    const [subMenuOptions, setSubMenuOptions] = useState<
      Array<{ label: string; value: string; icon?: string }>
    >([]);
    const [selectedOutput, setSelectedOutput] = useState<string | null>(
      getSelectedOutputValue,
    );

    if (
      loopSource?.oneofKind !== "outputReference" &&
      loopSource?.oneofKind !== "recordList"
    ) {
      return null;
    }

    const isWholeRecordSelected = selectedOutput === WHOLE_RECORD_VALUE;
    const source =
      loopSource.oneofKind === "outputReference"
        ? loopSource.outputReference.source
        : loopSource.recordList.recordList.oneofKind === "actionReference"
          ? {
              oneofKind: "actionReference" as const,
              actionReference: loopSource.recordList.recordList.actionReference,
            }
          : undefined;

    if (source === undefined) {
      return null;
    }

    const type:
      | "triggerReference"
      | "actionReference"
      | "loopItem"
      | undefined = source.oneofKind;
    const triggerId = candidate.self.trigger.triggerReference?.triggerId;
    const selectedActionReference =
      source.oneofKind === "actionReference"
        ? source.actionReference
        : undefined;
    const selectedActionId = selectedActionReference?.actionId;

    const isRecordSource =
      (type === "triggerReference" &&
        RECORD_CREATED_TRIGGER_IDS.includes(triggerId ?? "")) ||
      (type === "actionReference" &&
        [ActionLabel.createRecord, ActionLabel.updateRecord].includes(
          selectedActionId as ActionLabel,
        ));

    const outputs: OutputDescription[] | undefined =
      outputReferenceToOptionValue(
        // outputReferenceToOptionValue (and the findWebhookAction it calls
        // internally) only read `.source` off the store they're given -- a
        // minimal stand-in avoids needing a real scoped LoopingStore just for
        // this read (precedent: looping.store.ts's own call for the same
        // pattern).
        { source: loopSource } as unknown as LoopingStore,
        candidate.self.trigger,
        candidate.allActions,
      );
    const sourceAction = selectedActionReference
      ? candidate.allActions.find(
          (action) =>
            action.actionReference?.instanceId ===
            selectedActionReference.instanceId,
        )
      : undefined;
    const canSelectWholeRecord = isSingleFindRecordsAction(sourceAction);
    const isEditingAutomation = candidate.self.automationId !== "";
    const shouldShowWholeRecordOption =
      canSelectWholeRecord && isEditingAutomation && isWholeRecordSelected;
    const shouldUseFieldCopy = isRecordSource || canSelectWholeRecord;
    const selectedOption = outputs?.find(
      (output) => output.outputId === selectedOutput,
    );
    const fieldMissingError =
      selectedOutput !== null &&
      !isWholeRecordSelected &&
      selectedOption === undefined
        ? String(t("Field is missing"))
        : "";
    const updateSelectedOutput = (nextSelectedOutput: string): void => {
      if (
        selectedActionReference !== undefined &&
        selectedOutput !== null &&
        selectedOutput !== nextSelectedOutput &&
        (selectedOutput === WHOLE_RECORD_VALUE ||
          nextSelectedOutput === WHOLE_RECORD_VALUE)
      ) {
        if (loopInstanceId !== undefined) {
          looping.setActionsWithOutReferencesAt(
            loopInstanceId,
            undefined,
            selectedActionReference,
            CURRENT_ITEM_OUTPUT_ID,
          );
        } else {
          looping.setActionsWithOutReferences(
            undefined,
            selectedActionReference,
            CURRENT_ITEM_OUTPUT_ID,
          );
        }
      }

      setSelectedOutput(nextSelectedOutput);
    };
    const selectedPlaceholderOptions = isWholeRecordSelected
      ? [
          {
            label: t("looping.whole-record", {
              defaultValue: "Whole record",
            }),
            value: WHOLE_RECORD_VALUE,
          },
        ]
      : selectedOutput && selectedOption
        ? [
            {
              label: getOutputLabel(selectedOption),
              value: selectedOutput,
            },
          ]
        : [];
    const outputMenuOptions = buildOutputMenuOptions(outputs ?? []);
    const filteredOutputMenuOptions = outputMenuOptions
      .map((option) => {
        if (search.trim().length === 0) {
          return option;
        }

        const searchValue = search.toLocaleLowerCase();

        if (option.nestedOptions !== undefined) {
          const nestedOptions = option.nestedOptions.filter((nestedOption) =>
            nestedOption.label.toLocaleLowerCase().includes(searchValue),
          );

          if (
            option.label.toLocaleLowerCase().includes(searchValue) &&
            nestedOptions.length === 0
          ) {
            return option;
          }

          return nestedOptions.length > 0
            ? {
                ...option,
                nestedOptions,
              }
            : null;
        }

        return option.label.toLocaleLowerCase().includes(searchValue)
          ? option
          : null;
      })
      .filter((option) => option !== null);
    const hasNoLoopFields =
      filteredOutputMenuOptions.length === 0 && !shouldShowWholeRecordOption;
    const selectorInputGroupClasses = classNames(
      "react-input-group",
      "react-input-group--white",
      {
        "is-error": fieldMissingError !== "",
        "is-default": !isOpen,
        "is-active": isOpen,
      },
    );

    useEffect(() => {
      setSelectedOutput(getSelectedOutputValue());
    }, [loopSource]);

    useEffect(() => {
      if (!isOpen) {
        setTab("root");
        setSubMenuTitle("");
        setSubMenuOptions([]);
        setSearch("");
      }
    }, [isOpen]);

    useEffect(() => {
      if (
        selectedOutput === WHOLE_RECORD_VALUE &&
        selectedActionReference !== undefined
      ) {
        setLoopSource({
          oneofKind: "recordList",
          recordList: {
            recordList: {
              oneofKind: "actionReference",
              actionReference: selectedActionReference,
            },
            ...recordListConfigFrom(sourceAction?.description.recordListOutput),
          },
        });
        return;
      }

      if (selectedOutput && selectedOption) {
        if (
          type === "triggerReference" &&
          candidate.self.trigger.triggerReference !== undefined
        ) {
          setLoopSource({
            oneofKind: "outputReference",
            outputReference: {
              outputId: selectedOutput,
              source: {
                oneofKind: "triggerReference",
                triggerReference: candidate.self.trigger.triggerReference,
              },
            },
          });
        }
        if (type === "actionReference") {
          if (selectedActionReference !== undefined) {
            setLoopSource({
              oneofKind: "outputReference",
              outputReference: {
                outputId: selectedOutput,
                source: {
                  oneofKind: "actionReference",
                  actionReference: selectedActionReference,
                },
              },
            });
          }
        }
      }
    }, [
      candidate.allActions,
      candidate.self.trigger.triggerReference,
      loopInstanceId,
      looping,
      selectedActionReference,
      selectedOption,
      selectedOutput,
      type,
    ]);

    return (
      <StyledWrapper>
        <StyledHeader>
          <Text weight="medium">
            {shouldUseFieldCopy
              ? t("looping.loop-by-field", {
                  defaultValue: "Loop by field",
                })
              : t("looping.webhook-response")}
          </Text>
          <Text size="xs" color="secondary">
            {shouldUseFieldCopy
              ? t("looping.record-from-trigger-description", {
                  defaultValue: "Choose a field to loop through its values",
                })
              : t("looping.webhook-response-description")}
          </Text>
        </StyledHeader>
        <StyledSelector>
          <DropdownPanel
            enableWidthCalculation={true}
            my="left top+2"
            at="left bottom+5"
            portalStyles={getLocalCssColorVariables(theme)}
            isActive={isOpen}
            targetElementRef={buttonRef}
            onClickOutside={() => {
              setIsOpen(false);
            }}
          >
            {tab === "root" && (
              <MenuGroupContent
                searchPlaceholder={"Search Fields"}
                onSearchChange={(text) => setSearch(text)}
                search={
                  outputMenuOptions.length > 5 || search.trim().length > 0
                }
                maxHeight={400}
                smallListPaddings={true}
              >
                {shouldShowWholeRecordOption && (
                  <MenuOption
                    key={WHOLE_RECORD_VALUE}
                    onClick={() => {
                      updateSelectedOutput(WHOLE_RECORD_VALUE);
                      setIsOpen(false);
                    }}
                  >
                    {t("looping.whole-record", {
                      defaultValue: "Whole record",
                    })}
                  </MenuOption>
                )}
                {hasNoLoopFields && (
                  <StyledNoFields>
                    {t("looping.no-fields-to-loop-through", {
                      defaultValue: "No fields to loop through",
                    })}
                  </StyledNoFields>
                )}
                {filteredOutputMenuOptions.map((option) => {
                  if (option.nestedOptions !== undefined) {
                    return (
                      <MenuOption
                        key={option.label}
                        submenuArrow="right"
                        onClick={() => {
                          setSubMenuTitle(option.label);
                          setSubMenuOptions(option.nestedOptions);
                          setTab("sub-menu");
                        }}
                      >
                        <>
                          {option.icon && (
                            <StyledIcon>
                              <Icon
                                name={option.icon}
                                size="S"
                                color={SolutionColorVariables.sc1}
                              />
                            </StyledIcon>
                          )}
                          {option.label}
                        </>
                      </MenuOption>
                    );
                  }

                  return (
                    <MenuOption
                      key={option.value}
                      onClick={() => {
                        updateSelectedOutput(option.value);
                        setIsOpen(false);
                      }}
                    >
                      <>
                        {option.icon && (
                          <StyledIcon>
                            <Icon
                              name={option.icon}
                              size="S"
                              color={SolutionColorVariables.sc1}
                            />
                          </StyledIcon>
                        )}
                        {option.label}
                      </>
                    </MenuOption>
                  );
                })}
              </MenuGroupContent>
            )}
            {tab === "sub-menu" && (
              <MenuGroupContent
                title={subMenuTitle}
                search={false}
                maxHeight={400}
                smallListPaddings={true}
                onNavigateBack={() => setTab("root")}
              >
                {subMenuOptions
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((nestedOption) => (
                    <MenuOption
                      key={nestedOption.value}
                      onClick={() => {
                        updateSelectedOutput(nestedOption.value);
                        setIsOpen(false);
                      }}
                    >
                      <>
                        {nestedOption.icon && (
                          <StyledIcon>
                            <Icon
                              name={nestedOption.icon}
                              size="S"
                              color={SolutionColorVariables.sc1}
                            />
                          </StyledIcon>
                        )}
                        {nestedOption.label}
                      </>
                    </MenuOption>
                  ))}
              </MenuGroupContent>
            )}
          </DropdownPanel>
          <StyledSelectorInput
            className={selectorInputGroupClasses}
            data-testid="webhook-output-selector-input"
          >
            <div
              className="single-select single-select--type-regular"
              data-testid="webhook-output-selector"
            >
              <div className="single-select__wrap">
                <div
                  className="single-select__placeholder"
                  role="presentation"
                  ref={buttonRef}
                  onClick={() => setIsOpen(!isOpen)}
                  onKeyDown={() => setIsOpen(!isOpen)}
                >
                  <SelectPlaceholder
                    active={isOpen}
                    options={selectedPlaceholderOptions}
                    placeholder={
                      isOpen ? t("automation-form.select-field") : "\u00a0"
                    }
                    placeholderArrowStyle="filled-dark"
                    onClick={() => {}}
                    type="regular"
                    renderOption={(option) => (
                      <Paragraph wrapMode="ellipsis" color="currentColor">
                        {String(option.label)}
                      </Paragraph>
                    )}
                    showClear={false}
                  />
                </div>
              </div>
            </div>
          </StyledSelectorInput>
        </StyledSelector>
        <FieldMissingError message={fieldMissingError} />
      </StyledWrapper>
    );
  },
);
